import { haversineKm, segmentDistance } from "./mobileScreenUtils";

export const orderedCoordinates = (route) =>
  [...(route.routeCoordinates || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

export const stopIndex = (coords, stop) => stop ? coords.findIndex(coord =>
  Math.abs(coord.latitude - stop.latitude) < 0.0001 &&
  Math.abs(coord.longitude - stop.longitude) < 0.0001
) : -1;

export const journeyDirection = (coords, origin, destination) => {
  const from = stopIndex(coords, origin);
  const to = stopIndex(coords, destination);
  if (from < 0 || to < 0 || from === to) return null;
  return from < to ? "source_to_dest" : "dest_to_source";
};

export const routeAllowsJourney = (route, origin, destination) => {
  const coords = orderedCoordinates(route);
  if (!journeyDirection(coords, origin, destination)) return false;
  const from = stopIndex(coords, origin);
  const to = stopIndex(coords, destination);
  const name = (destination.name || "").trim().toLowerCase().replace(/\s+/g, " ");
  if (name === "manipal hospital" && from < to && from !== to - 1) return false;
  if (name === "bial t1-departure" && from > to && from !== to + 1) return false;
  return true;
};

// Project onto segments instead of snapping to a stop: a bus halfway between
// stops must not be treated as already at either stop.
const routePosition = (coords, live) => {
  let bestDistance = Infinity;
  let position = null;
  let travelled = 0;
  const scale = Math.cos(Number(live.latitude) * Math.PI / 180);
  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i];
    const b = coords[i + 1];
    const dx = (b.longitude - a.longitude) * scale;
    const dy = b.latitude - a.latitude;
    const px = (live.longitude - a.longitude) * scale;
    const py = live.latitude - a.latitude;
    const lengthSquared = dx * dx + dy * dy;
    const fraction = lengthSquared ? Math.max(0, Math.min(1, (px * dx + py * dy) / lengthSquared)) : 0;
    const distance = (px - fraction * dx) ** 2 + (py - fraction * dy) ** 2;
    const length = haversineKm(a, b);
    if (length === null) return null;
    if (distance < bestDistance) {
      bestDistance = distance;
      position = travelled + fraction * length;
    }
    travelled += length;
  }
  return position;
};

export const vehicleJourney = (coords, origin, destination, live, direction, radiusMeters = 60) => {
  const desired = journeyDirection(coords, origin, destination);
  if (!desired || !["source_to_dest", "dest_to_source"].includes(direction)) return null;
  if (!live || live.latitude == null || live.longitude == null ||
      !Number.isFinite(Number(live.latitude)) || !Number.isFinite(Number(live.longitude))) return null;
  const position = routePosition(coords, live);
  if (position === null) return null;
  const originPosition = segmentDistance(coords, 0, stopIndex(coords, origin));
  const total = segmentDistance(coords, 0, coords.length - 1);
  const atOrigin = haversineKm(live, origin) * 1000 <= radiusMeters;
  if (direction === desired) {
    const distanceKm = desired === "source_to_dest" ? originPosition - position : position - originPosition;
    if (distanceKm < 0 && !atOrigin) return null;
    return { distanceKm: Math.max(0, distanceKm), atStop: atOrigin, turningAround: false };
  }
  // Opposite-direction buses first finish their current leg at the actual
  // route endpoint, then return to the boarding stop in the requested direction.
  const distanceKm = desired === "source_to_dest"
    ? position + originPosition
    : (total - position) + (total - originPosition);
  const terminalOrigin = desired === "source_to_dest" ? originPosition === 0 : originPosition === total;
  return { distanceKm, atStop: atOrigin && terminalOrigin, turningAround: true };
};
